import { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { BLOCK_TYPES, FMT_TYPES, fmtDesc, fmtNote, mmss, type Block, type BlockKind, type ComplexFormat, type FormatKind } from '@tg/domain';

/* Тип блока и настройка комплекса (CON-18) — как openFtype в прототипе: поповер у чипа типа.
   Тип и формат — выпадающими списками (вариантов много, кнопки загромождали). Описание настроек комплекса
   пишется в комментарий блока — клиент видит его под заголовком; тренер правит комментарий как обычно.
   Комментарий, который тренер написал сам, настройки не перезаписывают. */
export function TypePicker({ block, anchor, onChange, onClose }: { block: Block; anchor: HTMLElement; onChange: (p: Partial<Block>) => void; onClose: () => void }) {
  const box = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ left: 0, top: 0 });
  const b = block;
  const f = b.fmt;

  useLayoutEffect(() => {
    const el = box.current;
    if (!el) return;
    const r = anchor.getBoundingClientRect();
    const W = document.documentElement.clientWidth;
    const H = innerHeight;
    const below = r.bottom + 6 + el.offsetHeight <= H - 8;
    setPos({
      left: Math.max(12, Math.min(r.left, W - el.offsetWidth - 12)),
      top: window.scrollY + (below ? r.bottom + 6 : Math.max(8, r.top - el.offsetHeight - 6)),
    });
  }, [anchor, b.kind, f?.k]);

  useLayoutEffect(() => {
    /* Список выпадающего поля (app-core/dropdowns.ts) живёт в body — клик и Enter в нём не закрывают поповер */
    const inList = (t: EventTarget | null) => t instanceof Element && !!t.closest('.dsl');
    const out = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node) && e.target !== anchor && !inList(e.target)) onClose();
    };
    const key = (e: KeyboardEvent) => (e.key === 'Escape' || e.key === 'Enter') && !inList(e.target) && !document.querySelector('.dsl') && onClose();
    setTimeout(() => document.addEventListener('mousedown', out));
    document.addEventListener('keydown', key);
    return () => {
      document.removeEventListener('mousedown', out);
      document.removeEventListener('keydown', key);
    };
  }, []);

  /* Смена настроек комплекса — описание в комментарий, если комментарий пуст или это прежнее описание */
  const change = (p: Partial<Block>) => {
    if ('fmt' in p && (!b.note || b.note === fmtNote(f))) p = { ...p, note: fmtNote(p.fmt ?? null) };
    onChange(p);
  };
  const setFmt = (patch: Partial<ComplexFormat>) => {
    if (!f) return;
    const n = { ...f, ...patch };
    if (n.k === 'EMOM') n.total = n.rounds * n.work;
    if (n.k === 'TABATA' || n.k === 'INTERVALS') n.total = n.rounds * (n.work + n.rest);
    if (n.k === 'AMRAP') n.work = n.total;
    change({ fmt: n });
  };
  const ms = (key: 'total' | 'work' | 'rest', sec: number) => (
    <>
      <input className="st-n" type="number" min={0} placeholder="0" value={Math.floor(sec / 60)} onChange={(e) => setFmt({ [key]: (+e.target.value || 0) * 60 + (sec % 60) })} />
      <i>мин</i>
      <input className="st-n" type="number" min={0} max={59} step={5} placeholder="00" value={sec % 60} onChange={(e) => setFmt({ [key]: Math.floor(sec / 60) * 60 + (+e.target.value || 0) })} />
      <i>сек</i>
    </>
  );
  const rounds = <input className="st-n" type="number" min={1} value={f?.rounds ?? 1} onChange={(e) => setFmt({ rounds: Math.max(1, +e.target.value || 1) })} />;
  /* Готовые интервалы и свой — минуты и секунды вручную: «1:15», «1:45», «5:00» */
  const seg = (vals: number[]) => (
    <>
      <span className="st-seg">
        {vals.map((w) => (
          <button key={w} className={f?.work === w ? 'on' : ''} onClick={() => setFmt({ work: w })}>
            {mmss(w)}
          </button>
        ))}
      </span>
      {f && (
        <span className="st-own">
          <i>или</i>
          {ms('work', f.work)}
        </span>
      )}
    </>
  );
  const params = () => {
    if (!f) return null;
    switch (f.k) {
      case 'AMRAP':
        return (
          <>
            <span className="st-l">Время</span>
            <span className="st-v">{ms('total', f.total)}</span>
          </>
        );
      case 'EMOM':
        return (
          <>
            <span className="st-l">Раундов</span>
            <span className="st-v">
              {rounds}
              <s className="st-res">всего {mmss(f.total)}</s>
            </span>
            <span className="st-l">Интервал</span>
            <span className="st-v">{seg([60, 90, 120, 180])}</span>
          </>
        );
      case 'FOR_TIME':
        return (
          <>
            <span className="st-l">Раундов</span>
            <span className="st-v">
              {rounds}
              <s className="st-res">1 — один проход (чиппер)</s>
            </span>
            <span className="st-l">Лимит</span>
            <span className="st-v">
              {ms('total', f.total)}
              <s className="st-res">0 — без лимита</s>
            </span>
          </>
        );
      case 'TABATA':
      case 'INTERVALS':
        return (
          <>
            <span className="st-l">Раундов</span>
            <span className="st-v">{rounds}</span>
            <span className="st-l">Работа</span>
            <span className="st-v">{ms('work', f.work)}</span>
            <span className="st-l">Отдых</span>
            <span className="st-v">{ms('rest', f.rest)}</span>
          </>
        );
      case 'DEATH_BY':
        return (
          <>
            <span className="st-l">Интервал</span>
            <span className="st-v">{seg([60, 90, 120])}</span>
            <span className="st-l">Старт</span>
            <span className="st-v">
              <input className="st-n" type="number" min={1} value={f.start || 1} onChange={(e) => setFmt({ start: Math.max(1, +e.target.value || 1) })} />
              <i>повт</i>
            </span>
          </>
        );
    }
    return null;
  };

  return createPortal(
    <div ref={box} className="sug setup ftp" style={pos}>
      <div className="st-head">
        <b>Тип блока</b>
      </div>
      <select
        className="inp ft-sel"
        value={b.kind || ''}
        aria-label="Тип блока"
        onChange={(e) => {
          const k = (e.target.value || null) as BlockKind | null;
          change({ kind: k, ...(k !== 'complex' ? { fmt: null } : {}) });
        }}
      >
        {BLOCK_TYPES.map((t) => (
          <option key={t.k || 'none'} value={t.k || ''}>
            {t.n}
          </option>
        ))}
      </select>
      {b.kind === 'complex' && (
        <>
          <div className="st-head cx">
            <b>Настройка комплекса</b>
            <s className="st-desc">{f ? fmtDesc(f) : ''}</s>
          </div>
          <select
            className="inp ft-sel"
            value={f?.k || ''}
            aria-label="Настройка комплекса"
            onChange={(e) => {
              const t = FMT_TYPES.find((x) => x.k === (e.target.value as FormatKind));
              change({ fmt: t ? { k: t.k, ...t.d } : null });
            }}
          >
            <option value="">без настройки</option>
            {FMT_TYPES.map((t) => (
              <option key={t.k} value={t.k}>
                {t.n}
              </option>
            ))}
          </select>
          {f && f.k !== 'NFT' && <div className="st-grid">{params()}</div>}
        </>
      )}
      <div className="st-foot">
        <span className="sp"></span>
        <button className="btn sm" data-st-ok onClick={onClose}>
          Готово ↵
        </button>
      </div>
    </div>,
    document.body,
  );
}
